import SelectField from '../shared/SelectField';
import { ANY_GENRE, getLibraryGenres } from '../../utils/libraryFilters';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Share2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useWishlist } from '../../context/WishlistContext';

const PanelContainer = styled.aside`
  width: 320px;
  background: ${({ theme }) => theme.isDarkMode ? '#101113' : '#FBFBFB'};
  border-left: 0px solid ${({ theme }) => theme.colors.border};
  height: calc(100vh - 64px);
  position: fixed;
  right: var(--vk-offset, 0px);
  top: 64px;
  padding: 20px;
  overflow-y: auto;
  z-index: 100;
  transition: background-color 0.3s ease, border-color 0.3s ease;

  &::-webkit-scrollbar { display: none; }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: 1200px) { width: 280px; }
  @media (max-width: 1024px) { display: none; }
`;

const SectionContainer = styled.div`
  background: ${({ theme }) => theme.isDarkMode ? '#18191B' : '#EFF3F7'};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 10px;
  transition: background-color 0.3s ease;
`;

const Section = styled.section`
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 16px;
`;

const SectionSubtitle = styled.h4`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 12px;
  font-weight: 500;
  margin-top: 20px;
  &:first-child { margin-top: 0; }
`;

const LargeNumber = styled.div`
  font-size: 40px;
  font-weight: 700;
  color: ${({ theme }) => theme.isDarkMode ? '#EFF3F7' : '#101113'};
  margin-bottom: 2px;
`;

const SmallText = styled.span`
  font-size: 8px;
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.7;
`;

const AboutText = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.5;
  margin-bottom: 10px;
`;

const FilterPillContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
`;

const FilterPill = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  background: ${props => props.$active ? '#18191B' : '#D9D9D9'};
  color: ${props => props.$active ? 'white' : '#101113'};
  &:hover { opacity: 0.9; }
`;


const YearInputContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
`;

const YearInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.isDarkMode ? '#101113' : '#FBFBFB'};
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
  &::placeholder { color: ${({ theme }) => theme.colors.textSecondary}; }
  &::-webkit-outer-spin-button, &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  -moz-appearance: textfield;
`;

const ResetButton = styled.button`
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: none;
  background: #D9D9D9;
  color: #101113;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: #C9C9C9; }
`;

const ShareButton = styled.button`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: none;
  background: #18191B;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &:hover { background: #28292B; }
`;

const WishlistRightPanel = ({
  activeContentType, setActiveContentType,
  activeStatus, setActiveStatus,
  genre, setGenre,
  yearFrom, setYearFrom,
  yearTo, setYearTo,
  resetFilters
}) => {
  const { t } = useTranslation();
  const { wishlistItems, getStats } = useWishlist();
  const stats = getStats();

  const lastUpdated = useMemo(() => {
    const sortedItems = [...wishlistItems].sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt));
    const lastItem = sortedItems[0];
    if (lastItem?.addedAt) {
      return new Date(lastItem.addedAt).toLocaleDateString();
    }
    return null;
  }, [wishlistItems]);

  const uniqueGenres = useMemo(() => [...new Set(wishlistItems.flatMap(item => getLibraryGenres(item.genres)))].sort(), [wishlistItems]);

  const handleShareWishlist = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: t('rightPanel.shareWishlist'),
          text: t('rightPanel.aboutWishlistText'),
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert(t('rightPanel.wishlistCopied'));
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        try {
          await navigator.clipboard.writeText(window.location.href);
          alert(t('rightPanel.wishlistCopied'));
        } catch {
          alert(t('rightPanel.shareError'));
        }
      }
    }
  };

  return (
    <PanelContainer>
      <SectionContainer>
        <Section>
          <SectionTitle>{t('rightPanel.aboutWishlist')}</SectionTitle>
          <AboutText>{t('rightPanel.aboutWishlistText')}</AboutText>
          <LargeNumber>{stats.total}</LargeNumber>
          <span>{t('rightPanel.projects')}</span>
          {lastUpdated && <SmallText> {t('rightPanel.updated')} {lastUpdated}</SmallText>}
        </Section>
      </SectionContainer>

      <SectionContainer>
        <Section>
          <SectionTitle>{t('rightPanel.filters')}</SectionTitle>
          <SectionSubtitle>{t('rightPanel.contentType')}</SectionSubtitle>
          <FilterPillContainer>
            <FilterPill $active={activeContentType === 'all'} onClick={() => setActiveContentType('all')}>{t('rightPanel.all')}</FilterPill>
            <FilterPill $active={activeContentType === 'film'} onClick={() => setActiveContentType('film')}>{t('rightPanel.films')}</FilterPill>
            <FilterPill $active={activeContentType === 'series'} onClick={() => setActiveContentType('series')}>{t('rightPanel.series')}</FilterPill>
            <FilterPill $active={activeContentType === 'game'} onClick={() => setActiveContentType('game')}>{t('rightPanel.games')}</FilterPill>
            <FilterPill $active={activeContentType === 'music'} onClick={() => setActiveContentType('music')}>{t('rightPanel.music')}</FilterPill>
            <FilterPill $active={activeContentType === 'book'} onClick={() => setActiveContentType('book')}>{t('rightPanel.books')}</FilterPill>
          </FilterPillContainer>
          <SectionSubtitle>{t('rightPanel.status')}</SectionSubtitle>
          <FilterPillContainer>
            <FilterPill $active={activeStatus === 'all'} onClick={() => setActiveStatus('all')}>{t('rightPanel.wantToSee')}</FilterPill>
            <FilterPill $active={activeStatus === 'watching'} onClick={() => setActiveStatus('watching')}>{t('rightPanel.watching')}</FilterPill>
            <FilterPill $active={activeStatus === 'dropped'} onClick={() => setActiveStatus('dropped')}>{t('rightPanel.dropped')}</FilterPill>
          </FilterPillContainer>
          <SelectField label={t('rightPanel.genre')} value={genre} onValueChange={setGenre}
            options={[{ value: ANY_GENRE, label: t('rightPanel.anyGenre') }, ...uniqueGenres.map(value => ({ value, label: value }))]} />
          <SectionSubtitle>{t('rightPanel.year')}</SectionSubtitle>
          <YearInputContainer>
            <YearInput type="number" placeholder={t('rightPanel.yearFrom')} value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} min="1900" max="2100" />
            <YearInput type="number" placeholder={t('rightPanel.yearTo')} value={yearTo} onChange={(e) => setYearTo(e.target.value)} min="1900" max="2100" />
          </YearInputContainer>
          <ResetButton onClick={() => resetFilters()}>{t('rightPanel.resetFilters')}</ResetButton>
        </Section>
      </SectionContainer>

      <SectionContainer>
        <Section>
          <SectionTitle>{t('rightPanel.quickActions')}</SectionTitle>
          <ShareButton onClick={handleShareWishlist}>
            <Share2 size={16} />
            {t('rightPanel.shareWishlist')}
          </ShareButton>
        </Section>
      </SectionContainer>
    </PanelContainer>
  );
};

export default WishlistRightPanel;